import { useTranslation } from 'react-i18next';
import { PencilIcon } from '@heroicons/react/24/outline';

import { DisplayModes } from 'services/webedi/types';
import { Alert } from 'components/Display/Alert';
import { Button } from 'components/Form/Button';
import { SearchInput } from 'components/SearchInput/SearchInput';
import { AnnotationNavigationActions } from 'components/WebEDI/AnnotationNavigationActions/AnnotationNavigationActions';
import { AnnotationsLabel } from 'components/WebEDI/WebEDIMessageErrorBanner/components/AnnotationsLabel';

import { ProductMasterdataMode } from '../../hooks/useProductMasterdataMode';
import { ProductValidationService } from '../../helpers/ProductValidationService';

export type Props = {
  mode: ProductMasterdataMode;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onEnterEditMode: () => void;
  onCancelEdit: () => void;
  onSave: () => void;
  /** Carries the errors of a rejected save, which the edit-mode banner counts and cycles through. */
  validation: ProductValidationService;
  isSaving: boolean;
  isLoading: boolean;
};

export const ProductMasterdataToolbar = ({
  mode,
  searchQuery,
  onSearchChange,
  onEnterEditMode,
  onCancelEdit,
  onSave,
  validation,
  isSaving,
  isLoading,
}: Props) => {
  const { t } = useTranslation();
  const isEditing = mode === DisplayModes.edit;
  const errorCount = validation.useNumberOfCycleableItems();

  return (
    <div className="mb-3 space-y-3">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="mb-1 text-base font-bold text-black">{t('settings:productMasterdata.title')}</h3>
          <p className="text-sm text-gray-500">{t('settings:productMasterdata.subTitle')}</p>
        </div>

        {!isEditing && (
          // Search sits next to Edit products, not above/below it, because the two are mutually
          // exclusive: search is meaningless once editing starts (the whole point is disabling it
          // to protect unsaved rows from a refetch), so there is no view where both are usable at
          // once. Editing hides this whole side — Cancel/Save move into the edit-mode banner below
          // — matching that same "one or the other" relationship visually, not just behaviorally.
          <div className="flex shrink-0 items-center gap-2">
            <SearchInput
              filter={searchQuery}
              placeholder={t('settings:productMasterdata.search.placeholder')}
              onFilterChange={onSearchChange}
            />
            <Button
              variant="secondary"
              LeftIcon={PencilIcon}
              onClick={onEnterEditMode}
              disabled={isLoading}
              analyticsId="product-masterdata:enter_edit_mode"
            >
              {t('settings:productMasterdata.actions.editProducts')}
            </Button>
          </div>
        )}
      </div>

      {isEditing && errorCount > 0 ? (
        // Save is gone while anything is outstanding: the server refused these values, so sending
        // them again would only be refused again. The banner turns back into the blue one, with
        // Save, once every error has been dealt with.
        <Alert withoutIcon severity="error">
          <div className="flex w-full items-center justify-between gap-4">
            <p>{t('settings:productMasterdata.validation.errorBanner.description')}</p>
            <div className="flex shrink-0 items-center gap-2">
              <AnnotationNavigationActions
                leadingContent={
                  <AnnotationsLabel
                    variant="error"
                    label={t('settings:productMasterdata.validation.errorCount', { count: errorCount })}
                  />
                }
                totalNumberOfAnnotations={errorCount}
                onPrev={validation.moveToPreviousItem}
                onNext={validation.moveToNextItem}
                prevAnalyticsId="product-masterdata:previous_error"
                nextAnalyticsId="product-masterdata:next_error"
                onFocus={validation.resetFocus}
                focusAnalyticsId="product-masterdata:errors_focus_first"
                focusLabel={t('webedi:viewDocument.errorBanner.focusOnError')}
              />
              <Button
                variant="secondary"
                size="small"
                onClick={onCancelEdit}
                disabled={isSaving}
                analyticsId="product-masterdata:cancel_edit"
              >
                {t('common:cancel')}
              </Button>
            </div>
          </div>
        </Alert>
      ) : null}

      {isEditing && errorCount === 0 ? (
        <Alert
          severity="info"
          actions={{
            secondary: {
              children: t('common:cancel'),
              onClick: onCancelEdit,
              disabled: isSaving,
              analyticsId: 'product-masterdata:cancel_edit',
            },
            primary: {
              children: t('settings:productMasterdata.actions.save'),
              onClick: onSave,
              loading: isSaving,
              analyticsId: 'product-masterdata:save_changes',
            },
          }}
        >
          {t('settings:productMasterdata.editModeBanner')}
        </Alert>
      ) : null}
    </div>
  );
};
